import { TableHeader } from "../components/custom-table/table";

/**
 * Candidate column keys that may absorb the removed icons column's width.
 * Includes both `medication` and its org-specific replacement
 * `appointmentType` (used by Sunstate Urology) so the redistribution still
 * sums to 100% regardless of which variant is rendered. The actual divisor
 * is computed from how many of these are present in the current header
 * set, not the array length.
 */
const COLUMNS_TO_EXPAND = ["medication", "appointmentType", "planDetails"];

/**
 * Adjusts table headers based on the `enable_review_mode` flag.
 *
 * When review mode is disabled (false or undefined), removes the icons
 * column and redistributes its width evenly across whichever of the
 * {@link COLUMNS_TO_EXPAND} keys are present in the header set. This keeps
 * the total column width at 100% even when some columns have been swapped
 * out by org-specific overrides (e.g. Sunstate Urology replacing
 * `medication` with `appointmentType`).
 *
 * @param headers - The original table headers array
 * @param enableReviewMode - Whether review mode is enabled for the health
 *   facility
 * @param iconsColumnKey - Key of the icons column to conditionally
 *   show/hide (default: `"orderProcessAndReviewStatus"`)
 * @param expandAllColumns - Distribute the removed column's space proportionally
 *   across every remaining column, as required by the Pending layout
 * @returns Adjusted table headers array whose widths still sum to 100
 */
export const adjustTableHeadersForReviewMode = (
  headers: TableHeader[],
  enableReviewMode?: boolean,
  iconsColumnKey: string = "orderProcessAndReviewStatus",
  expandAllColumns: boolean = false,
): TableHeader[] => {
  if (enableReviewMode) {
    return headers;
  }

  const iconsColumn = headers.find((header) => header.key === iconsColumnKey);
  const iconsColumnWidth = iconsColumn?.width ?? 0;

  const filteredHeaders = headers.filter(
    (header) => header.key !== iconsColumnKey,
  );

  if (expandAllColumns) {
    const totalWidth = filteredHeaders.reduce(
      (sum, header) => sum + header.width,
      0,
    );
    return totalWidth > 0
      ? filteredHeaders.map((header) => ({
          ...header,
          width: (header.width / totalWidth) * 100,
        }))
      : filteredHeaders;
  }

  // Only the expand-eligible columns that actually exist in the current
  // header set should absorb the freed width — divide by their count to
  // keep the total at 100. Bail out if none are present so we don't divide
  // by zero (the freed width is intentionally not redistributed in that
  // edge case).
  const expandableColumnCount = filteredHeaders.filter((header) =>
    COLUMNS_TO_EXPAND.includes(header.key),
  ).length;

  if (expandableColumnCount === 0) {
    return filteredHeaders;
  }

  const extraWidthPerColumn = iconsColumnWidth / expandableColumnCount;

  return filteredHeaders.map((header) =>
    COLUMNS_TO_EXPAND.includes(header.key)
      ? { ...header, width: header.width + extraWidthPerColumn }
      : header,
  );
};
